'use client';

import { useMemo, useState } from 'react';
import { OptionContext } from './contexts/OptionContext';
import { HiddenOptionsAndValues, Monogram, OptionsSelection, StylePreset, Option } from '../utils/interfaces';
import { ConfigurationTypes } from '../utils/productDefinition';

export type OptionContextProviderProps = {
  children: React.ReactNode;
  defaultSelectedOptions: OptionsSelection;
  defaultHiddenOptionsAndValues: HiddenOptionsAndValues;
  defaultSelectedMonogram: Monogram;
  defaultSelectedBaseStyle: Record<string, StylePreset>;
  defaultIsAdditionalProductAdded: Record<string, boolean>;
};

export const OptionContextProvider = ({
  children,
  defaultSelectedOptions,
  defaultHiddenOptionsAndValues,
  defaultSelectedMonogram,
  defaultSelectedBaseStyle,
  defaultIsAdditionalProductAdded,
}: OptionContextProviderProps) => {
  const [selectedOptions, setSelectedOptions] = useState<OptionsSelection>(defaultSelectedOptions);
  const [hiddenOptionsAndValues, setHiddenOptionsAndValues] =
    useState<HiddenOptionsAndValues>(defaultHiddenOptionsAndValues);
  const [selectedMonogram, setSelectedMonogram] = useState<Monogram>(defaultSelectedMonogram);
  const [selectedBaseStyle, setSelectedBaseStyle] = useState<Record<string, StylePreset>>(defaultSelectedBaseStyle);
  const [isAdditionalProductAdded, setIsAdditionalProductAdded] = useState<Record<string, boolean>>(
    defaultIsAdditionalProductAdded,
  );
  const [isBaseStyleSelection, setIsBaseStyleSelection] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<Option | undefined>(undefined);
  const [selectedOptionGroup, setSelectedOptionGroup] = useState<Option | undefined>(undefined);
  const [animation, setAnimation] = useState<{ status: boolean; from: Option | undefined; to: Option | undefined }>({
    status: false,
    from: undefined,
    to: undefined,
  });
  const [isProductAddedModalShown, setIsProductAddedModalShown] = useState<Record<string, boolean>>({});
  const [isRemoveProductModalShown, setIsRemoveProductModalShown] = useState<Record<string, boolean>>({});

  const selectOption = (option: OptionsSelection) => {
    setSelectedOptions(prev => ({ ...prev, ...option }));
  };

  const updateHiddenOptionsAndValues = (hiddenOptionsAndValues: HiddenOptionsAndValues) => {
    setHiddenOptionsAndValues(prev => ({ ...prev, ...hiddenOptionsAndValues }));
  };

  const addMonogram = (monogram: Monogram) => {
    setSelectedMonogram(prev => ({ ...prev, ...monogram }));
  };

  const deleteMonogram = (monogramId: number) => {
    setSelectedMonogram(prev => {
      const next = { ...prev };
      delete next[monogramId];
      return next;
    });
  };

  const selectBaseStyle = (configurationType: ConfigurationTypes, baseStyle: StylePreset) => {
    setSelectedBaseStyle(prev => ({ ...prev, [configurationType]: baseStyle }));
  };

  const removeBaseStyle = (configurationType: ConfigurationTypes) => {
    setSelectedBaseStyle(prev => {
      const next = { ...prev };
      delete next[configurationType];
      return next;
    });
  };

  const addRemoveAdditionalProduct = (configurationType: ConfigurationTypes, isAdded: boolean) => {
    setIsAdditionalProductAdded(prev => ({ ...prev, [configurationType]: isAdded }));
  };

  const clearMonograms = () => {
    setSelectedMonogram({});
  };

  const updateIsProductAddedModalShown = (configurationType: ConfigurationTypes, showModal: boolean) => {
    setIsProductAddedModalShown(prev => ({ ...prev, [configurationType]: showModal }));
  };

  const updateIsRemoveProductModalShown = (configurationType: ConfigurationTypes, showModal: boolean) => {
    setIsRemoveProductModalShown(prev => ({ ...prev, [configurationType]: showModal }));
  };

  const contextValue = useMemo(
    () => ({
      selectedOptions,
      setSelectedOptions,
      selectOption,
      hiddenOptionsAndValues,
      setHiddenOptionsAndValues,
      updateHiddenOptionsAndValues,
      selectedMonogram,
      setSelectedMonogram,
      addMonogram,
      deleteMonogram,
      clearMonograms,
      selectedBaseStyle,
      setSelectedBaseStyle,
      selectBaseStyle,
      removeBaseStyle,
      isAdditionalProductAdded,
      setIsAdditionalProductAdded,
      addRemoveAdditionalProduct,
      isBaseStyleSelection,
      setIsBaseStyleSelection,
      selectedOption,
      setSelectedOption,
      selectedOptionGroup,
      setSelectedOptionGroup,
      animation,
      setAnimation,
      isProductAddedModalShown,
      setIsProductAddedModalShown,
      updateIsProductAddedModalShown,
      isRemoveProductModalShown,
      setIsRemoveProductModalShown,
      updateIsRemoveProductModalShown,
    }),
    [
      selectedOptions,
      hiddenOptionsAndValues,
      selectedMonogram,
      selectedBaseStyle,
      isAdditionalProductAdded,
      isBaseStyleSelection,
      selectedOption,
      selectedOptionGroup,
      animation,
      isProductAddedModalShown,
      isRemoveProductModalShown,
    ],
  );

  return <OptionContext.Provider value={contextValue}>{children}</OptionContext.Provider>;
};
